import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, Zap, ArrowRight, Copy, Clock, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function ThankYou() {
  const location = useLocation();
  const navigate = useNavigate();
  const { publicToken } = location.state || {};

  const statusUrl = publicToken ? `${window.location.origin}/status/${publicToken}` : null;

  const copyLink = () => {
    if (statusUrl) {
      navigator.clipboard.writeText(statusUrl);
      toast.success('Status link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4 py-12">
      <Card className="max-w-lg w-full rounded-2xl shadow-[var(--shadow-lift)] border-2 border-[hsl(var(--border))]">
        <CardContent className="p-8 text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-9 w-9 text-green-600" />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--brand-slate-900)] mb-2">Your Quote Request is Confirmed</h1>
            <p className="text-[var(--brand-slate-600)] leading-relaxed">
              We're reviewing your photos and will send your pricing options soon.
            </p>
            <p className="text-sm text-[var(--brand-ocean-700)] mt-2 font-medium">
              You'll receive a confirmation email shortly.
            </p>
          </div>

          <div className="bg-[var(--brand-ocean-100)]/30 rounded-2xl p-5 text-left">
            <p className="font-heading font-semibold text-[var(--brand-ocean-700)] mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" /> What happens next:
            </p>
            <ol className="space-y-2 text-sm text-[var(--brand-slate-600)]">
              <li className="flex items-start gap-2">
                <span className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">1</span>
                <span>We review your photos today</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">2</span>
                <span>You receive pricing within 24 hours</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">3</span>
                <span>Compare options with no pressure</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">4</span>
                <span>We handle permits and installation</span>
              </li>
            </ol>
          </div>

          <div className="bg-[var(--brand-seafoam-200)]/20 rounded-xl p-4 border border-[var(--brand-seafoam-200)]">
            <p className="text-sm text-[var(--brand-ocean-700)] flex items-center justify-center gap-2">
              <Phone className="h-4 w-4" />
              <span>Questions? Call or text us: <a href="tel:8557847627" className="font-semibold hover:underline">855-784-SNAP</a></span>
            </p>
          </div>

          {statusUrl && (
            <div className="space-y-3 pt-2">
              <p className="text-sm font-medium text-[var(--brand-slate-900)]">Track your quote status anytime:</p>
              <div className="flex items-center gap-2 bg-[hsl(var(--secondary))] border-2 border-[hsl(var(--border))] rounded-xl p-3">
                <code className="text-xs flex-1 truncate text-[var(--brand-ocean-700)] font-mono text-left">{statusUrl}</code>
                <Button variant="ghost" size="sm" onClick={copyLink} className="shrink-0 h-8 hover:bg-white">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <Button onClick={() => navigate(`/status/${publicToken}`)} variant="outline" className="rounded-xl w-full h-11 border-2 font-semibold">
                View Quote Status <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          <div className="pt-4 border-t">
            <Button onClick={() => navigate('/')} variant="ghost" className="text-[var(--brand-slate-600)] hover:text-[var(--brand-ocean-700)]">
              <Zap className="h-4 w-4 mr-2" /> Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
